import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { RefreshCw, Warehouse } from 'lucide-react'

import type { DashboardGroupBy } from '@/hooks/use-warehouse-dashboard-query'
import type { MovementMode } from '@/components/charts/movement-breakdown-chart'
import { PageBackButton } from '@/components/common/PageBackButton'
import { MovementBreakdownChart } from '@/components/charts/movement-breakdown-chart'
import { TopMoversCheckboxTable } from '@/components/charts/top-movers-checkbox-table'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { InventoryKpiCards } from '@/components/warehouse/inventory-kpi-cards'
import { InventoryPeriodFilter } from '@/components/warehouse/inventory-period-filter'
import { LowStockTable } from '@/components/warehouse/low-stock-table'
import { useAuth } from '@/hooks/use-auth'
import {
    useDefects,
    useInventorySummary,
    useLowStockProducts,
    useMovementBreakdown,
    warehouseDashboardKeys,
} from '@/hooks/use-warehouse-dashboard-query'
import { useLotDebtOverview } from '@/hooks/use-sales-order-query'
import { getPresetRange } from '@/lib/date-presets'
import { m } from '@/lib/paraglide/messages'

const PERMISSION = 'warehouse_sales.read'

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/warehouse-sales/dashboard',
)({
    component: WarehouseSalesDashboardPage,
})

function WarehouseSalesDashboardPage() {
    const { hasPermission, arePermissionsLoaded } = useAuth()

    if (arePermissionsLoaded && !hasPermission(PERMISSION)) {
        return <ForbiddenState />
    }

    return <DashboardContent />
}

function DashboardContent() {
    const initialRange = getPresetRange('7d')
    const [dateFrom, setDateFrom] = useState(initialRange.from)
    const [dateTo, setDateTo] = useState(initialRange.to)
    const [groupBy, setGroupBy] = useState<DashboardGroupBy>('day')
    const [selectedIds, setSelectedIds] = useState<string[]>([])
    const [mode, setMode] = useState<MovementMode>('in')
    const queryClient = useQueryClient()
    const refresh = () => {
        void queryClient.invalidateQueries({
            queryKey: warehouseDashboardKeys.all,
        })
    }

    const rangeParams = {
        date_from: dateFrom,
        date_to: dateTo,
    }
    const rangedGroup = { ...rangeParams, group_by: groupBy }

    const { data: summary, isLoading: summaryLoading } = useInventorySummary()
    const { data: breakdown, isLoading: breakdownLoading } =
        useMovementBreakdown({ ...rangedGroup, top_n: 10 })
    const { data: defects = [], isLoading: defectsLoading } =
        useDefects(rangedGroup)
    const { data: lowStock = [], isLoading: lowStockLoading } =
        useLowStockProducts(10)
    // Tồn đọng nợ lô (FD-372) — việc phải làm trong ngày của kho, khác các ô
    // còn lại vốn là số liệu để đọc.
    const { data: lotDebt } = useLotDebtOverview()

    const groupLabel = (() => {
        switch (groupBy) {
            case 'week':
                return m.warehouse_sales_group_week()
            case 'month':
                return m.warehouse_sales_group_month()
            case 'year':
                return m.warehouse_sales_group_year()
            default:
                return m.warehouse_sales_group_day()
        }
    })()

    return (
        <div className="space-y-6 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                    <PageBackButton className="-my-2 md:-my-1" />
                    <Warehouse className="size-5" />
                    <div>
                        <h1 className="text-xl font-semibold">
                            {m.warehouse_sales_dashboard_title()}
                        </h1>
                        <p className="text-sm text-muted-foreground">
                            {m.warehouse_sales_dashboard_subtitle()}
                        </p>
                    </div>
                </div>
                <Button variant="outline" size="sm" onClick={refresh}>
                    <RefreshCw className="size-4 mr-1" />
                    {m.common_refresh()}
                </Button>
            </div>

            <Tabs defaultValue="overview" className="space-y-4">
                <TabsList className="w-fit self-center">
                    <TabsTrigger value="overview">
                        {m.warehouse_sales_tab_overview()}
                    </TabsTrigger>
                    <TabsTrigger value="status">
                        {m.warehouse_sales_tab_status()}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-6">
                    <Card>
                        <CardContent className="pt-6">
                            <InventoryPeriodFilter
                                dateFrom={dateFrom}
                                dateTo={dateTo}
                                groupBy={groupBy}
                                onDateFromChange={setDateFrom}
                                onDateToChange={setDateTo}
                                onGroupByChange={setGroupBy}
                            />
                        </CardContent>
                    </Card>

                    <InventoryKpiCards
                        summary={summary}
                        defects={defects}
                        isLoading={summaryLoading || defectsLoading}
                        dateFrom={dateFrom}
                        dateTo={dateTo}
                        lotDebt={lotDebt}
                    />

                    {breakdownLoading ? (
                        <Card>
                            <CardContent className="py-12 text-center text-muted-foreground">
                                {m.common_loading()}
                            </CardContent>
                        </Card>
                    ) : (
                        // 2-col layout: bảng top bên trái, chart bên phải.
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            <TopMoversCheckboxTable
                                entities={breakdown?.top_entities ?? []}
                                selectedIds={selectedIds}
                                onSelectionChange={setSelectedIds}
                            />
                            <MovementBreakdownChart
                                rows={breakdown?.rows ?? []}
                                topEntities={breakdown?.top_entities ?? []}
                                selectedIds={selectedIds}
                                mode={mode}
                                onModeChange={setMode}
                                groupLabel={groupLabel}
                            />
                        </div>
                    )}
                </TabsContent>

                <TabsContent value="status" className="space-y-6">
                    {/* SP sắp hết hàng + Lô sắp hết hạn — ngang hàng 2 cột. */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <LowStockTable
                            items={lowStock}
                            isLoading={lowStockLoading}
                        />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    )
}

function ForbiddenState() {
    return (
        <Card>
            <CardContent className="py-12 text-center space-y-2">
                <h2 className="text-lg font-semibold">
                    {m.warehouse_sales_forbidden_title()}
                </h2>
                <p className="text-sm text-muted-foreground">
                    {m.warehouse_sales_forbidden_desc()}
                </p>
            </CardContent>
        </Card>
    )
}
